import React, { useState } from 'react';
import { Button, Radio, TextField, Card, CardContent } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Toast from '@/utils/toastManager';
import { setField, setIsClass } from '@/redux/formData';
import axiosConfig from '@/data/utils/axiosConfigInstance';
import { fileUploadApi } from '@/data/api/fileUpload';
import Spinner from '@/component/Spinner';

const UploadViaAssetId = ({ dispatch }) => {
	const [selectedValue, setSelectedValue] = useState('library');
	const [uploadState, setUploadState] = useState({
		thumbnail: { loading: false },
		trailerThumbnail: { loading: false },
		loading: false
	});
	const [preview, setPreview] = useState(null);
	const [trailerPreview, setTrailerPreview] = useState(null);
	const [assetId, setAssetId] = useState('');
	const [trailerAssetId, setTrailerAssetId] = useState('');

	const handleTypeSelect = e => {
		const isClass = e.target.value === 'class';
		dispatch(setIsClass(isClass));
		setSelectedValue(e.target.value);
	};

	const handleImageChange = async (e, type = 'main') => {
		const file = e.target.files[0];
		if (!file) return;

		const key = type === 'trailer' ? 'trailerThumbnail' : 'thumbnail';
		setUploadState(prev => ({ ...prev, [key]: { loading: true } }));

		try {
			const uploadResponse = await fileUploadApi(file);
			const cdnLink = uploadResponse?.data?.cdnLink;

			if (type === 'trailer') {
				dispatch(setField('trailerThumnailLink', cdnLink));
				setTrailerPreview(URL.createObjectURL(file));
			} else {
				dispatch(setField('thumnailLink', cdnLink));
				setPreview(URL.createObjectURL(file));
			}
		} catch (err) {
			console.error('Image Upload Failed:', err);
		} finally {
			setUploadState(prev => ({ ...prev, [key]: { loading: false } }));
		}
	};

	const fetchMuxAssetData = async (id, type = 'video') => {
		if (!id.trim()) return;
		setUploadState(prev => ({ ...prev, loading: true }));

		try {
			const { data } = await axiosConfig.get(`${import.meta.env.VITE_APP_API_URL}/admin/mux-asset?assetId=${id}`);

			if (!data?.success) {
				Toast.error('Failed to fetch Mux asset data. Please verify the Asset ID.');
				return;
			}
			const title = data.data.title;
			const muxLink = data.data.muxVideoLink;
			const duration = data.data.duration;
			const playbackId = data.data.playback_id;

			if (type === 'video') {
				dispatch(setField('title', title));
				dispatch(setField('muxVideoLink', muxLink));
				dispatch(setField('duration', Math.floor(duration || 0)));
				dispatch(setField('playbackId', playbackId));
			} else {
				dispatch(setField('trailerTitle', `Trailer - ${title}`));
				dispatch(setField('muxTrailerLink', muxLink));
				dispatch(setField('trailerDuration', Math.floor(duration || 0)));
				dispatch(setField('trailerPlaybackId', playbackId));
			}
			Toast.success(`Fetched Mux data for ${type === 'video' ? 'main' : 'trailer'} video.`);
		} catch (error) {
			Toast.error(error.message || 'Error fetching Mux asset data. Please verify the Asset ID.');
		} finally {
			setUploadState(prev => ({ ...prev, loading: false }));
		}
	};

	return (
		<>
			{uploadState.loading && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100vw',
						height: '100vh',
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						zIndex: 9999,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					<Spinner />
				</div>
			)}

			<Card className="p-3" style={{ position: 'relative' }}>
				<CardContent>
					<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: '2rem' }}>
						{/* Mux Asset ID input */}
						<div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
							<TextField
								label="Mux Asset ID (Main Video)"
								fullWidth
								value={assetId}
								onChange={e => setAssetId(e.target.value)}
							/>
							<Button
								variant="contained"
								startIcon={<CloudUploadIcon />}
								disabled={!assetId.trim()}
								onClick={() => fetchMuxAssetData(assetId, 'video')}
							>
								Fetch
							</Button>
						</div>

						{/* Thumbnail Upload */}
						<div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
							<span>Upload Video Thumbnail</span>
							<Button component="label" variant="contained" startIcon={<CloudUploadIcon />}>
								{uploadState.thumbnail.loading ? 'Uploading...' : 'Upload Image'}
								<input type="file" accept="image/*" hidden onChange={e => handleImageChange(e, 'main')} />
							</Button>
							{preview && (
								<div className="mt-2">
									<img
										src={preview}
										alt="Preview"
										style={{ width: '6.25rem', height: '6.25rem', objectFit: 'cover' }}
									/>
								</div>
							)}
						</div>
					</div>

					{/* Type Selection */}
					<div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
						<span style={{ fontSize: '1rem', fontWeight: 600 }}>Select the type of video</span>

						<label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
							<Radio
								checked={selectedValue === 'library'}
								onChange={handleTypeSelect}
								value="library"
								name="radio-buttons"
								inputProps={{ 'aria-label': 'Library' }}
							/>
							Library
						</label>

						<label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
							<Radio
								checked={selectedValue === 'class'}
								onChange={handleTypeSelect}
								value="class"
								name="radio-buttons"
								inputProps={{ 'aria-label': 'Class' }}
							/>
							Class
						</label>
					</div>

					{/* Conditional Trailer Input */}
					{selectedValue === 'class' && (
						<>
							{/* Trailer Video */}
							<div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
								<TextField
									label="Mux Asset ID (Trailer)"
									fullWidth
									value={trailerAssetId}
									onChange={e => setTrailerAssetId(e.target.value)}
								/>
								<Button
									variant="contained"
									startIcon={<CloudUploadIcon />}
									disabled={!trailerAssetId.trim()}
									onClick={() => fetchMuxAssetData(trailerAssetId, 'trailer')}
								>
									Fetch
								</Button>
							</div>

							{/* Trailer Thumbnail */}
							<div style={{ display: 'flex', gap: '2rem', alignItems: 'center', marginTop: '2rem' }}>
								<span>Upload Trailer Thumbnail</span>
								<Button component="label" variant="contained" startIcon={<CloudUploadIcon />}>
									{uploadState.trailerThumbnail.loading ? 'Uploading...' : 'Upload Trailer Thumbnail'}
									<input type="file" accept="image/*" hidden onChange={e => handleImageChange(e, 'trailer')} />
								</Button>
								{trailerPreview && (
									<div className="mt-2">
										<img
											src={trailerPreview}
											alt="Preview"
											style={{ width: '6.25rem', height: '6.25rem', objectFit: 'cover' }}
										/>
									</div>
								)}
							</div>
						</>
					)}
				</CardContent>
			</Card>
		</>
	);
};

export default UploadViaAssetId;
